import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { useLearningProfileStore } from "../../lib/stores/learningProfileStore";
import { getCurriculumApproachDisplay, AGE_GROUP_OPTIONS, COGNITIVE_LEVEL_OPTIONS } from "../../lib/soloAssessmentLogic";
import { Icons } from "../../lib/Icons";
import html2pdf from "html2pdf.js";

const LearningProfileResults = () => {
    const navigate = useNavigate();
    const { profileId } = useParams();
    const location = useLocation();
    const printRef = useRef(null);

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [profileName, setProfileName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [savedProfileId, setSavedProfileId] = useState(profileId || null);

    const {
        profileAnswers,
        questionnaireAnswers,
        curriculumApproach,
        recommendedActivities,
        assessmentSummary,
        generateResults,
        getProfileData,
        loadProfile,
        reset
    } = useLearningProfileStore();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                navigate('/login');
                return;
            }
            setUser(currentUser);

            // Check if coming from builder (fresh results) or loading existing
            const fromBuilder = location.state?.fromBuilder;

            if (fromBuilder && profileAnswers.ageGroup) {
                // Generate fresh results from builder
                setGenerating(true);
                try {
                    const kbSnapshot = await getDocs(collection(db, 'kb_items'));
                    const kbItems = kbSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    await generateResults(kbItems);
                    setProfileName(`Profile - ${new Date().toLocaleDateString()}`);
                } catch (err) {
                    console.error('Error generating results:', err);
                }
                setGenerating(false);
            } else if (profileId && profileId !== 'results') {
                // Load existing profile
                try {
                    const profileDoc = await getDoc(doc(db, 'learningProfiles', profileId));
                    if (profileDoc.exists()) {
                        const data = profileDoc.data();
                        loadProfile({ id: profileDoc.id, ...data });
                        setProfileName(data.name || 'Untitled Profile');
                        setSavedProfileId(profileDoc.id);

                        // We need to regenerate recommendations with KB items
                        const kbSnapshot = await getDocs(collection(db, 'kb_items'));
                        const kbItems = kbSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        await generateResults(kbItems);
                    } else {
                        navigate('/solo/profiles');
                    }
                } catch (err) {
                    console.error('Error loading profile:', err);
                    navigate('/solo/profiles');
                }
            } else if (!profileAnswers.ageGroup) {
                // No data - redirect to builder
                navigate('/solo/profiles/new');
                return;
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate, profileId, location.state, profileAnswers, generateResults, loadProfile]);

    const handleSaveProfile = async () => {
        if (!user) return;

        setSaving(true);
        try {
            const profileData = getProfileData();
            profileData.name = profileName || 'Untitled Profile';
            profileData.userId = user.uid;

            if (savedProfileId) {
                // Update existing
                await updateDoc(doc(db, 'learningProfiles', savedProfileId), {
                    ...profileData,
                    updatedAt: serverTimestamp()
                });
            } else {
                // Create new
                const newDocRef = doc(collection(db, 'learningProfiles'));
                await setDoc(newDocRef, {
                    ...profileData,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                setSavedProfileId(newDocRef.id);
            }

            setIsEditingName(false);
        } catch (err) {
            console.error('Error saving profile:', err);
        }
        setSaving(false);
    };

    // Generate print-friendly HTML for PDF export - Classroom wall-friendly design
    const generatePrintableHTML = () => {
        const approachInfo = curriculumApproach ? getCurriculumApproachDisplay(curriculumApproach) : null;
        const ageLabelText = AGE_GROUP_OPTIONS.find(a => a.id === profileAnswers.ageGroup)?.label || 'Not specified';
        const cogLabelText = COGNITIVE_LEVEL_OPTIONS.find(c => c.id === profileAnswers.cognitiveLevel)?.label || 'Not specified';
        const approachText = curriculumApproach === 'ENGAGEMENT_MODEL' ? 'Engagement Model' : 'NC with Support';
        const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        const approachColor = curriculumApproach === 'ENGAGEMENT_MODEL' ? '#7C3AED' : '#2563EB';
        const approachBg = curriculumApproach === 'ENGAGEMENT_MODEL' ? '#F5F3FF' : '#EFF6FF';
        const approachBorder = curriculumApproach === 'ENGAGEMENT_MODEL' ? '#8B5CF6' : '#3B82F6';

        // Domain color mapping for colorful badges
        const getDomainColor = (domain) => {
            const colors = {
                'Communication': { bg: '#DBEAFE', text: '#1D4ED8', border: '#3B82F6' },
                'SEMH': { bg: '#D1FAE5', text: '#065F46', border: '#10B981' },
                'Cognition': { bg: '#EDE9FE', text: '#5B21B6', border: '#8B5CF6' },
                'Sensory/Physical': { bg: '#FFEDD5', text: '#C2410C', border: '#F97316' },
            };
            return colors[domain] || { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
        };

        // Match score color (green for high, yellow for medium, gray for low)
        const getMatchColor = (score) => {
            if (score >= 0.8) return { bg: '#D1FAE5', text: '#065F46' };
            if (score >= 0.6) return { bg: '#FEF3C7', text: '#92400E' };
            return { bg: '#F3F4F6', text: '#6B7280' };
        };

        // Build activities HTML using table layout for compatibility
        let activitiesRows = '';
        if (recommendedActivities.length > 0) {
            for (let i = 0; i < recommendedActivities.length; i += 2) {
                const activity1 = recommendedActivities[i];
                const activity2 = recommendedActivities[i + 1];
                
                const activityCell = (activity) => {
                    if (!activity) return '<td style="width: 50%; padding: 8px;"></td>';
                    
                    // Colorful domain badges
                    const tags = (activity.targetDomains || []).slice(0, 2).map(d => {
                        const color = getDomainColor(d);
                        return `<span style="display: inline-block; background-color: ${color.bg}; color: ${color.text}; border: 1px solid ${color.border}; padding: 3px 8px; border-radius: 12px; font-size: 11px; margin-right: 4px; font-weight: 500;">${d}</span>`;
                    }).join('');
                    
                    const ageTag = activity.ageRange ? 
                        `<span style="display: inline-block; background-color: #F3F4F6; color: #4B5563; padding: 3px 8px; border-radius: 12px; font-size: 11px; border: 1px solid #E5E7EB;">Ages ${activity.ageRange.min}-${activity.ageRange.max}</span>` : '';
                    
                    const matchColor = getMatchColor(activity.matchScore);
                    
                    return `
                        <td style="width: 50%; padding: 8px; vertical-align: top;">
                            <div style="background-color: #FFFFFF; border: 2px solid #E5E7EB; border-radius: 12px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                                <table style="width: 100%; border-collapse: collapse;">
                                    <tr>
                                        <td style="padding: 0;">
                                            <strong style="font-size: 15px; color: #1F2937; line-height: 1.3;">${activity.title}</strong>
                                        </td>
                                        <td style="padding: 0; text-align: right; width: 60px; vertical-align: top;">
                                            <span style="display: inline-block; background-color: ${matchColor.bg}; color: ${matchColor.text}; padding: 4px 10px; border-radius: 20px; font-size: 13px; font-weight: 700;">${Math.round(activity.matchScore * 100)}%</span>
                                        </td>
                                    </tr>
                                </table>
                                <p style="font-size: 12px; color: #6B7280; margin: 10px 0; line-height: 1.5;">${(activity.description || '').substring(0, 100)}${(activity.description || '').length > 100 ? '...' : ''}</p>
                                <div style="margin-top: 8px;">${tags}${ageTag}</div>
                            </div>
                        </td>
                    `;
                };
                
                activitiesRows += `<tr>${activityCell(activity1)}${activityCell(activity2)}</tr>`;
            }
        } else {
            activitiesRows = '<tr><td colspan="2" style="text-align: center; padding: 30px; color: #6B7280; font-size: 14px;">No activities matched this profile.</td></tr>';
        }

        return `
            <div style="font-family: Arial, Helvetica, sans-serif; padding: 25px; background-color: #FFFFFF; color: #1F2937; min-height: 100%;">
                <!-- Header with decorative top border -->
                <div style="border-top: 6px solid #6366F1; padding-top: 20px;">
                    <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
                        <tr>
                            <td style="text-align: center; padding-bottom: 15px;">
                                <table style="display: inline-table; border-collapse: collapse;">
                                    <tr>
                                        <td style="vertical-align: middle; padding-right: 12px;">
                                            <img src="${window.location.origin}/brand/Logo%20cropped.png" alt="SEN-Ed" style="height: 50px; width: auto;" crossorigin="anonymous" />
                                        </td>
                                        <td style="vertical-align: middle;">
                                            <span style="font-size: 28px; font-weight: 700; color: #4F46E5;">SEN-Ed</span>
                                        </td>
                                    </tr>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td style="text-align: center;">
                                <h1 style="font-size: 32px; color: #1F2937; margin: 0 0 8px 0; font-weight: 800;">Learning Profile</h1>
                                <p style="font-size: 14px; color: #6B7280; margin: 0;">Generated: ${dateStr}</p>
                            </td>
                        </tr>
                    </table>
                </div>

                <!-- Student Name Field - Prominent for handwriting -->
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 25px; background-color: #F8FAFC; border: 2px solid #E2E8F0; border-radius: 12px;">
                    <tr>
                        <td style="padding: 20px;">
                            <table style="width: 100%; border-collapse: collapse;">
                                <tr>
                                    <td style="width: 140px; vertical-align: bottom;">
                                        <span style="font-size: 18px; font-weight: 700; color: #334155;">Student Name:</span>
                                    </td>
                                    <td style="vertical-align: bottom; padding-left: 10px;">
                                        <div style="border-bottom: 2px solid #94A3B8; height: 28px; min-width: 300px;"></div>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>

                <!-- Curriculum Approach Banner -->
                ${approachInfo ? `
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
                    <tr>
                        <td style="background-color: ${approachBg}; border: 2px solid ${approachBorder}; border-radius: 12px; padding: 18px;">
                            <table style="width: 100%; border-collapse: collapse;">
                                <tr>
                                    <td style="width: 50px; vertical-align: top; padding-right: 12px;">
                                        <div style="width: 40px; height: 40px; background-color: ${approachBorder}; border-radius: 10px; text-align: center; line-height: 40px;">
                                            <span style="color: white; font-size: 20px;">✦</span>
                                        </div>
                                    </td>
                                    <td>
                                        <h3 style="font-size: 18px; color: ${approachColor}; margin: 0 0 6px 0; font-weight: 700;">${approachInfo.title}</h3>
                                        <p style="font-size: 13px; color: #374151; margin: 0; line-height: 1.5;">${approachInfo.description}</p>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>
                ` : ''}

                <!-- Profile Summary - More prominent -->
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;">
                    <tr>
                        <td style="padding-bottom: 12px; border-bottom: 3px solid #6366F1;">
                            <h3 style="font-size: 16px; color: #4F46E5; margin: 0; text-transform: uppercase; letter-spacing: 1px; font-weight: 700;">Profile Summary</h3>
                        </td>
                    </tr>
                </table>
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 25px;">
                    <tr>
                        <td style="width: 33.33%; padding: 16px; background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%); border: 2px solid #C7D2FE; border-radius: 12px 0 0 12px;">
                            <p style="font-size: 12px; color: #6366F1; margin: 0 0 6px 0; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Age Group</p>
                            <p style="font-size: 18px; color: #1F2937; margin: 0; font-weight: 700;">${ageLabelText}</p>
                        </td>
                        <td style="width: 33.33%; padding: 16px; background: linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%); border: 2px solid #BBF7D0; border-left: none;">
                            <p style="font-size: 12px; color: #16A34A; margin: 0 0 6px 0; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Cognitive Level</p>
                            <p style="font-size: 18px; color: #1F2937; margin: 0; font-weight: 700;">${cogLabelText}</p>
                        </td>
                        <td style="width: 33.33%; padding: 16px; background: linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 100%); border: 2px solid #F0ABFC; border-left: none; border-radius: 0 12px 12px 0;">
                            <p style="font-size: 12px; color: #A855F7; margin: 0 0 6px 0; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Approach</p>
                            <p style="font-size: 18px; color: #1F2937; margin: 0; font-weight: 700;">${approachText}</p>
                        </td>
                    </tr>
                </table>

                <!-- Recommended Activities Header -->
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
                    <tr>
                        <td style="padding-bottom: 12px; border-bottom: 3px solid #6366F1;">
                            <table style="width: 100%; border-collapse: collapse;">
                                <tr>
                                    <td>
                                        <h3 style="font-size: 16px; color: #4F46E5; margin: 0; text-transform: uppercase; letter-spacing: 1px; font-weight: 700;">Recommended Activities</h3>
                                    </td>
                                    <td style="text-align: right;">
                                        <span style="display: inline-block; background-color: #EEF2FF; color: #4F46E5; padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 600;">${recommendedActivities.length} activities matched</span>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>

                <!-- Activities Grid -->
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 25px;">
                    ${activitiesRows}
                </table>

                <!-- Footer -->
                <table style="width: 100%; border-collapse: collapse; margin-top: 15px; border-top: 2px solid #E5E7EB;">
                    <tr>
                        <td style="text-align: center; padding-top: 15px;">
                            <p style="font-size: 11px; color: #9CA3AF; margin: 0;">Generated by SEN-Ed | www.sen-ed.co.uk</p>
                        </td>
                    </tr>
                </table>
            </div>
        `;
    };

    const handleDownloadPDF = async () => {
        setGenerating(true);
        try {
            // Sanitize filename to remove slashes and other invalid characters
            const safeName = (profileName || 'learning-profile')
                .replace(/[^a-z0-9]/gi, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');

            // Generate HTML string directly (like exportTimetablePDF.js does)
            const htmlContent = generatePrintableHTML();

            const options = {
                margin: 10,
                filename: `${safeName}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            };

            // Pass HTML string directly to html2pdf (not a DOM element)
            await html2pdf().set(options).from(htmlContent).save();
        } catch (err) {
            console.error('Error generating PDF:', err);
        } finally {
            setGenerating(false);
        }
    };

    const handleNewProfile = () => {
        reset();
        navigate('/solo/profiles/new');
    };

    const handleEditProfile = () => {
        navigate('/solo/profiles/new', { state: { editing: true } });
    };

    const approachDisplay = curriculumApproach ? getCurriculumApproachDisplay(curriculumApproach) : null;
    const ageLabel = AGE_GROUP_OPTIONS.find(a => a.id === profileAnswers.ageGroup)?.label || '';
    const cogLabel = COGNITIVE_LEVEL_OPTIONS.find(c => c.id === profileAnswers.cognitiveLevel)?.label || '';

    if (loading || generating) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
                    <p className="text-slate-400">{generating ? 'Generating recommendations...' : 'Loading...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/solo/profiles" className="flex items-center gap-3">
                            <img
                                src="/brand/White logo on purple.png"
                                alt="SEN-Ed"
                                className="w-10 h-10 rounded-xl"
                            />
                            <span className="text-xl font-bold text-white">Learning Profiles</span>
                        </Link>

                        <div className="flex items-center gap-3">
                            <Link
                                to="/solo/profiles"
                                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                            >
                                <Icons.FolderOpen className="w-4 h-4" />
                                View All
                            </Link>
                            <button
                                onClick={handleDownloadPDF}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                            >
                                <Icons.Download className="w-4 h-4" />
                                Download PDF
                            </button>
                            <button
                                onClick={handleSaveProfile}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl transition-colors disabled:opacity-50"
                            >
                                <Icons.Upload className="w-4 h-4" />
                                {saving ? 'Saving...' : savedProfileId ? 'Update' : 'Save Profile'}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={printRef}>
                {/* Profile Name */}
                <div className="mb-8">
                    {isEditingName ? (
                        <div className="flex items-center gap-3">
                            <input
                                type="text"
                                value={profileName}
                                onChange={(e) => setProfileName(e.target.value)}
                                className="text-2xl font-bold bg-slate-800 text-white px-4 py-2 rounded-xl border border-slate-600 focus:border-purple-500 focus:outline-none"
                                autoFocus
                            />
                            <button
                                onClick={() => setIsEditingName(false)}
                                className="p-2 text-slate-400 hover:text-white"
                            >
                                <Icons.Check className="w-5 h-5" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsEditingName(true)}
                            className="flex items-center gap-2 group"
                        >
                            <h1 className="text-2xl font-bold text-white">{profileName || 'Untitled Profile'}</h1>
                            <Icons.PencilSquare className="w-5 h-5 text-slate-500 group-hover:text-slate-300 transition-colors" />
                        </button>
                    )}
                </div>

                {/* Curriculum Approach Banner */}
                {approachDisplay && (
                    <div className={`mb-8 rounded-2xl p-6 ${curriculumApproach === 'ENGAGEMENT_MODEL'
                        ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30'
                        : 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30'
                        }`}>
                        <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${curriculumApproach === 'ENGAGEMENT_MODEL' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                                }`}>
                                <Icons.Sparkles className={`w-6 h-6 ${curriculumApproach === 'ENGAGEMENT_MODEL' ? 'text-purple-400' : 'text-blue-400'
                                    }`} />
                            </div>
                            <div>
                                <h2 className={`text-lg font-semibold mb-2 ${curriculumApproach === 'ENGAGEMENT_MODEL' ? 'text-purple-300' : 'text-blue-300'
                                    }`}>
                                    {approachDisplay.title}
                                </h2>
                                <p className="text-slate-300">{approachDisplay.description}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Timetable CTA - Only show for Engagement Model approach - Positioned prominently after approach banner */}
                {curriculumApproach === 'ENGAGEMENT_MODEL' && recommendedActivities.length > 0 && (
                    <div className="mb-8 p-6 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl border border-cyan-500/30">
                        <div className="flex flex-col md:flex-row items-center gap-4">
                            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center">
                                <Icons.Calendar className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <h3 className="text-lg font-semibold text-white mb-1">Create a Weekly Timetable</h3>
                                <p className="text-sm text-slate-300">
                                    Build a structured weekly schedule with these recommended activities, including transition songs and break times.
                                </p>
                            </div>
                            <Link
                                to={`/solo/timetable/${savedProfileId || 'new'}`}
                                state={{ fromProfile: true }}
                                className="flex-shrink-0 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-medium rounded-xl transition-colors"
                            >
                                <Icons.Calendar className="w-5 h-5" />
                                Create Timetable
                            </Link>
                        </div>
                    </div>
                )}

                {/* Profile Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-slate-800 rounded-2xl p-6">
                        <h3 className="text-sm font-medium text-slate-400 mb-2">Age Group</h3>
                        <p className="text-lg text-white">{ageLabel}</p>
                    </div>
                    <div className="bg-slate-800 rounded-2xl p-6">
                        <h3 className="text-sm font-medium text-slate-400 mb-2">Cognitive Level</h3>
                        <p className="text-lg text-white">{cogLabel}</p>
                    </div>
                    <div className="bg-slate-800 rounded-2xl p-6">
                        <h3 className="text-sm font-medium text-slate-400 mb-2">Approach</h3>
                        <p className="text-lg text-white">
                            {curriculumApproach === 'ENGAGEMENT_MODEL' ? 'Engagement Model' : 'NC with Support'}
                        </p>
                    </div>
                </div>

                {/* Recommended Activities */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-white">Recommended Activities</h2>
                        <span className="text-slate-400">{recommendedActivities.length} activities matched</span>
                    </div>

                    {recommendedActivities.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {recommendedActivities.map((activity, idx) => (
                                <Link
                                    key={activity.id}
                                    to={`/solo/activity/${activity.id}`}
                                    className="bg-slate-800 rounded-2xl p-6 hover:bg-slate-700 transition-colors group"
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-medium text-white group-hover:text-purple-300 transition-colors">
                                                {activity.title}
                                            </h3>
                                        </div>
                                        <div className="flex items-center gap-2 px-2 py-1 bg-purple-500/20 rounded-lg">
                                            <span className="text-sm text-purple-300">{Math.round(activity.matchScore * 100)}%</span>
                                        </div>
                                    </div>

                                    <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                                        {activity.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                        {activity.targetDomains?.slice(0, 2).map(domain => (
                                            <span
                                                key={domain}
                                                className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs"
                                            >
                                                {domain}
                                            </span>
                                        ))}
                                        {activity.ageRange && (
                                            <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                                                Ages {activity.ageRange.min}-{activity.ageRange.max}
                                            </span>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-slate-800 rounded-2xl p-8 text-center">
                            <Icons.MagnifyingGlass className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">No activities matched this profile. Try adjusting the answers.</p>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4 justify-center pt-8 border-t border-slate-700">
                    <button
                        onClick={handleEditProfile}
                        className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                    >
                        <Icons.PencilSquare className="w-4 h-4" />
                        Edit Profile
                    </button>
                    <button
                        onClick={handleNewProfile}
                        className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                    >
                        <Icons.Plus className="w-4 h-4" />
                        New Profile
                    </button>
                    {curriculumApproach === 'ENGAGEMENT_MODEL' && recommendedActivities.length > 0 && (
                        <Link
                            to={`/solo/timetable/${savedProfileId || 'new'}`}
                            state={{ fromProfile: true }}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl transition-colors"
                        >
                            <Icons.Calendar className="w-4 h-4" />
                            Create Timetable
                        </Link>
                    )}
                    <Link
                        to={`/solo/search${recommendedActivities.length > 0 ? `?domain=${recommendedActivities[0]?.targetDomains?.[0] || ''}` : ''}`}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl transition-colors"
                    >
                        <Icons.MagnifyingGlass className="w-4 h-4" />
                        Explore More Activities
                    </Link>
                </div>
            </main>
        </div>
    );
};

export default LearningProfileResults;
